/**
 * The three figures above the list (RESP2c).
 *
 * All computed from the areas ALREADY loaded — no extra request, and
 * nothing that could disagree with the table underneath it. A KPI that
 * came from its own endpoint would eventually show a different number
 * than the rows it summarises.
 *
 * The third card reads "1 Organisation" and is not a lookup: this page
 * operates inside exactly one organisation by construction, so the
 * figure is a statement about scope rather than a measurement. It earns
 * its place by making that scope visible — a settings page that could
 * plausibly be organisation-wide should say which one it is.
 */
import React from "react";
import { Building2, Tags, Users } from "lucide-react";

import { Skeleton } from "../../../components/ui/skeleton";

function KpiCard({ icon: Icon, tone, value, title, subtitle, loading }) {
  return (
    <div className="rounded-[14px] border border-border bg-card p-5 shadow-[0_1px_2px_rgba(16,24,40,0.04)] dark:border-[#1e2130] dark:bg-[#13161f]">
      <div className="flex items-center gap-4">
        <span
          aria-hidden="true"
          className={
            "flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px] border " +
            tone
          }
        >
          <Icon className="h-5 w-5" strokeWidth={1.75} />
        </span>
        <div className="min-w-0">
          {loading ? (
            <Skeleton className="h-7 w-10" />
          ) : (
            <p className="m-0 text-[26px] font-bold leading-none text-foreground">
              {value}
            </p>
          )}
          <p className="m-0 mt-1.5 text-[13px] font-semibold text-foreground">
            {title}
          </p>
          <p className="m-0 mt-0.5 text-[12px] text-muted-foreground">
            {subtitle}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function ResponsibilityAreaKpis({ areas, loading }) {
  const list = Array.isArray(areas) ? areas : [];
  const active = list.filter((a) => a.is_active).length;
  // "Has a standing answer" — a main person OR at least one participant.
  // Both are real columns; neither is a team.
  const assigned = list.filter(
    (a) =>
      a.default_assigned_to_user_id ||
      (a.participant_user_ids || []).length > 0
  ).length;

  return (
    // Three across only from `xl`. At 1024 the shell's sidebar leaves each
    // of three cards about 200px, which clips "Standardverantwortliche"
    // mid-word — a 2+1 grid with readable cards beats three cropped ones.
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      <KpiCard
        icon={Tags}
        tone="border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300"
        value={active}
        title="Aktive Bereiche"
        subtitle="Organisationsweit konfiguriert"
        loading={loading}
      />
      <KpiCard
        icon={Users}
        tone="border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
        value={assigned}
        title="Standardverantwortliche"
        subtitle="Bereiche mit hinterlegten Personen"
        loading={loading}
      />
      <KpiCard
        icon={Building2}
        tone="border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300"
        value={1}
        title="Organisation"
        subtitle="Konfiguration aktiv"
        loading={false}
      />
    </div>
  );
}
